import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import { IFilterAllData, ListOfKeysFilterAllData } from 'src/typings/filter';
import { Actions } from 'src/store/app/actions';
import { getBitrixDeal, getCreatioDeal, getNetHuntDeal } from 'src/store/crosstable/selectors';
import { getCrmData, getSelectedAppartment, getIsOpenAppartSidebar } from 'src/store/app/selectors';
import { Actions as FloorsActions } from 'src/store/floors/actions';
import { CRMNames } from 'src/typings/app';
import { useBuildType, useGetFilteredData, UseNumberFormat } from 'src/hooks';
import {
  greyColor,
} from 'src/constants';
import { useContrastRatio } from 'src/hooks/useContrastRatio';
import { useCrosstableStatusToggle } from 'src/components/CrosstableContainer/hooks';
import { useSelectedIds } from '../../hooks';
import { useStyles } from '../../styles';

export interface IFloorItemBig {
  fillFloorsItem: IFilterAllData;
}

export const FloorItemBig: React.FC<IFloorItemBig> = React.memo(({
  fillFloorsItem,
}) => {
  const [isOpen, setIsOpen] = useState<Boolean>(false);
  const [isSelectedDeal, setIsSelectedDeal] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { isCrossTable } = useBuildType();
  const { selectedIds } = useSelectedIds();
  const isSelected = selectedIds.includes(fillFloorsItem.id);
  const { resultFiltering } = useGetFilteredData();

  const [backgroundColor] = useState<string>(isCrossTable
    ? (fillFloorsItem?.[ListOfKeysFilterAllData.placementStatusCrossTableColor]?.toString()
      || fillFloorsItem?.[ListOfKeysFilterAllData.placementStatusColor]?.toString() || '')
    : (fillFloorsItem?.[ListOfKeysFilterAllData.placementStatusColor]?.toString() || ''));
  const { getColorByBgColor } = useContrastRatio();

  const styles = useStyles({
    floors: fillFloorsItem[ListOfKeysFilterAllData.placementFloor].length,
  });
  const bitrixDeal = useSelector(getBitrixDeal);
  const creatioDeal = useSelector(getCreatioDeal);
  const netHuntDeal = useSelector(getNetHuntDeal);
  const crmData = useSelector(getCrmData);
  const selectedAppartment = useSelector(getSelectedAppartment);
  const isOpenAppartSidebar = useSelector(getIsOpenAppartSidebar);
  const id = (isOpen)
    ? 'floorItemPopover' : undefined;
  const { isStatusDisabledByStatusName } = useCrosstableStatusToggle();
  const { numberSeparator } = UseNumberFormat();

  useEffect(() => {
    if (isOpenAppartSidebar && selectedAppartment == fillFloorsItem[ListOfKeysFilterAllData.id]) {
      setIsOpen(true);
    } else if (isOpen) {
      setIsOpen(false);
    }
  }, [selectedAppartment, isOpenAppartSidebar]);

  useEffect(() => {
    if (fillFloorsItem) {
      const { bitrix_deals: bitrixDeals, netHuntOpportunities, creatioOpportunities } = fillFloorsItem;
      if (crmData?.crm === CRMNames.amocrm) {
        if (fillFloorsItem[ListOfKeysFilterAllData.placementAmoDeals]) {
          fillFloorsItem[ListOfKeysFilterAllData.placementAmoDeals].forEach((item) => {
            if (crmData && item.includes(String(crmData?.dealId))) {
              setIsSelectedDeal(true);
            }
          });
        }
      } else if (crmData?.crm === CRMNames.bitrix) {
        // Data-independent match first: the deal knows its linked unit — some
        // projects' rows carry no bitrix_deals arrays at all. Then the
        // string-safe array scan (UID string ids vs numeric ids never matched
        // with strict ===).
        const linkedUnitId = typeof bitrixDeal?.placement === 'string'
          ? (bitrixDeal.placement as string).split('/').filter(Boolean).pop()
          : bitrixDeal?.placement?.id;
        const isLinkedUnitRow = !!linkedUnitId
          && String(fillFloorsItem[ListOfKeysFilterAllData.id]) === String(linkedUnitId);
        const found = bitrixDeals && bitrixDeals.find((item) => {
          if (bitrixDeal && String(item.id) === String(bitrixDeal.id)) return true;
          if (bitrixDeal?.bitrixExternalDealId && item.bitrixExternalDealId
            && String(item.bitrixExternalDealId) === String(bitrixDeal.bitrixExternalDealId)) return true;
          return !!item.bitrixExternalDealId && crmData?.dealId != null
            && String(item.bitrixExternalDealId) === String(crmData.dealId);
        });
        setIsSelectedDeal(isLinkedUnitRow || !!found);
      } else if (crmData?.crm === CRMNames.creatio) {
        if (creatioOpportunities && creatioOpportunities.find((item) => creatioDeal && item.id === creatioDeal.id)) {
          setIsSelectedDeal(true);
        } else {
          setIsSelectedDeal(false);
        }
      } else if (crmData?.crm === CRMNames.netHunt) {
        if (netHuntOpportunities && netHuntOpportunities.find((item) => netHuntDeal && item.id === netHuntDeal.id)) {
          setIsSelectedDeal(true);
        } else {
          setIsSelectedDeal(false);
        }
      }
    }
  }, [fillFloorsItem, crmData, netHuntDeal, bitrixDeal, creatioDeal]);

  const handleClick = () => {
    if (!isSelected && !isCrossTable) return;

    if (isOpen) {
      dispatch(Actions.setIsOpenAppartSidebar(false));
      dispatch(FloorsActions.setSelectedApartmentId(undefined));
    } else {
      dispatch(FloorsActions.setSelectedApartmentId(fillFloorsItem[ListOfKeysFilterAllData.id]));
      dispatch(Actions.selectAppartment(fillFloorsItem[ListOfKeysFilterAllData.id]));
      dispatch(Actions.setIsOpenAppartSidebar(true));
    }
  };

  return (
    <div
      className={styles.sectionContentWrapperBig}
      data-selected-cell={isOpen ? 'true' : undefined}
      data-deal-cell={isSelectedDeal ? 'true' : undefined}
    >

      <Box
        aria-describedby={id}
        className={`${styles.sectionContentItemBig} ${(!isSelected || !resultFiltering.length) && 'blockedItem'} ${isSelectedDeal && 'selectedDeal'}`}
        data-floors={ListOfKeysFilterAllData.placementFloor}
        onClick={handleClick}
        position="relative"
      >
        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          mb={1}
        >
          <Box
            ml={1}
            mr="12px"
            px={1}
            minWidth="20px"
            height="20px"
            fontSize="10px"
            display="flex"
            justifyContent="center"
            fontWeight="bold"
            alignItems="center"
            borderRadius="12px"
            lineHeight="10px"
            style={{
              color: getColorByBgColor(backgroundColor),
              backgroundColor,
            }}
          >
            {fillFloorsItem[ListOfKeysFilterAllData.placementPlanningType]}
          </Box>
          {fillFloorsItem[ListOfKeysFilterAllData.placementTotalPrice] && !isStatusDisabledByStatusName(fillFloorsItem[ListOfKeysFilterAllData.placementStatus]) && (
          <Box fontWeight="bold">
            {
              // @ts-ignore
            `${numberSeparator(parseFloat(fillFloorsItem[ListOfKeysFilterAllData.placementTotalPrice]), ListOfKeysFilterAllData.placementTotalPrice)}`
}
          </Box>
          )}
        </Box>
        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          fontSize="11px"
        >
          {fillFloorsItem[ListOfKeysFilterAllData.placementNumeration] && (
          <Box
            minWidth="30px"
            mr="7px"
            textAlign="center"
            color={greyColor}
          >
            {`№${fillFloorsItem[ListOfKeysFilterAllData.placementNumeration]}`}
          </Box>
          )}
          {fillFloorsItem[ListOfKeysFilterAllData.placementTotalArea] && (
          <Box>
            {`${numberSeparator(fillFloorsItem[ListOfKeysFilterAllData.placementTotalArea] || '', ListOfKeysFilterAllData.placementTotalArea)}`}
          </Box>
          )}
        </Box>
      </Box>
    </div>
  );
});
